import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { coursesApi } from "api";
import { lessonLink } from "lib/paths";
import { statusCourses, watchCourse, messageCourse } from "store/actions/courses";

// Class content, the learner's progress and lesson navigation for the
// player. `params` are the route params { class, chapter, uid }.
export default function usePlayer(params, history, search = "") {
  const dispatch = useDispatch();
  const COURSES = useSelector((state) => state.courses);
  const courseId = params.class;
  const course = COURSES?.data?.[courseId];
  const [progress, setProgress] = useState(null);
  const [saving, setSaving] = useState(false);

  // Course content + this learner's progress.
  useEffect(() => {
    window.scroll(0, 0);
    dispatch(statusCourses("loading"));
    coursesApi
      .details(courseId)
      .then((res) => {
        if (res.chapters.length === 0) throw new Error("This class is not ready yet. Check back soon.");
        dispatch(watchCourse(res));
      })
      .catch((err) => dispatch(messageCourse(err?.response?.data?.message ?? err?.message ?? "error")));
    coursesApi
      .progress(courseId)
      .then(setProgress)
      .catch((err) => {
        // Not enrolled yet: go through the enrol step (free classes join
        // instantly and come straight back here).
        if (err?.response?.status === 404) history.replace(`/joined/${courseId}`);
        else setProgress(null);
      });
  }, [courseId, dispatch, history]);

  // Every lesson in order, for next/previous and the outline.
  const lessons = useMemo(
    () => (course?.chapters ?? []).flatMap((chapter) => (chapter.lessons ?? []).map((lesson) => ({ ...lesson, chapter }))),
    [course]
  );
  // ?lesson=<id> picks the exact lesson; chapter + video is the fallback.
  const lessonId = Number(new URLSearchParams(search).get("lesson"));
  const current =
    (lessonId && lessons.find((l) => l.id === lessonId)) ||
    lessons.find((l) => +l.chapter.id === +params.chapter && l.video === params.uid) ||
    null;

  // No lesson in the URL: open where the learner left off.
  useEffect(() => {
    if (!lessons.length || params.uid) return;
    const resume = progress?.resume;
    const target = (resume && lessons.find((l) => l.id === resume.lesson_id)) || lessons[0];
    history.replace(lessonLink(courseId, target));
  }, [lessons, progress, params.uid, courseId, history]);

  // Record the visit so "Continue" comes back here.
  useEffect(() => {
    if (!current) return;
    coursesApi.track(current.id).then(setProgress).catch(() => {});
  }, [current && current.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = new Set(progress?.completed_lesson_ids ?? []);
  const index = current ? lessons.findIndex((l) => l.id === current.id) : -1;
  const prev = index > 0 ? lessons[index - 1] : null;
  const next = index >= 0 ? lessons[index + 1] : null;
  const isDone = current ? done.has(current.id) : false;

  const setComplete = useCallback((lesson, value) => {
    setSaving(true);
    return coursesApi
      .track(lesson.id, value)
      .then(setProgress)
      .catch(() => {})
      .finally(() => setSaving(false));
  }, []);

  // Open any lesson; nothing is marked complete by moving around.
  const goTo = (lesson) => history.push(lessonLink(courseId, lesson));

  return {
    status: COURSES.status,
    message: COURSES.message,
    courseId,
    course,
    current,
    lessons,
    prev,
    next,
    goTo,
    done,
    isDone,
    percent: progress?.percent ?? 0,
    saving,
    setComplete,
  };
}
